import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, Lock, ShieldCheck, GraduationCap, Sparkles, BookOpen, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { BlurReveal } from "@/components/effects/blur-reveal";
import { PageFrame } from "@/components/page-frame";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/lib/auth-context";
import { updateProfile, completeOnboarding } from "@/lib/queries";
import { humaniseAuthError } from "@/lib/access";

function AuthShell({ children, title, subtitle, eyebrow }: {
  children: React.ReactNode; title: string; subtitle?: string; eyebrow?: string;
}) {
  return (
    <PageFrame surface="auth">
      <div className="min-h-screen relative">
        <div aria-hidden className="absolute inset-x-0 top-0 h-[500px] pointer-events-none"
          style={{ background: "radial-gradient(ellipse at top, rgba(201,169,106,0.18), transparent 60%)" }} />

        <Link to="/" className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-fg-2 hover:text-fg transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mx-auto max-w-md px-6 py-24 relative">
          <BlurReveal>
            <div className="flex items-center justify-center gap-3 mb-8">
              <img src="/logo.png" alt="" className="h-10 w-10 rounded-xl" />
              <span className="font-bold tracking-[0.15em] text-lg">ARABISE</span>
            </div>
          </BlurReveal>

          <BlurReveal delay={0.1}>
            <div className="text-center mb-6">
              {eyebrow && <div className="text-xs uppercase tracking-widest text-accent mb-2">{eyebrow}</div>}
              <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
              {subtitle && <p className="mt-2 text-sm text-fg-2 leading-relaxed">{subtitle}</p>}
            </div>
          </BlurReveal>

          <BlurReveal delay={0.2}>{children}</BlurReveal>
        </div>
      </div>
    </PageFrame>
  );
}

/* ───────────────────────── Onboarding ─────────────────────────
   Single-page: pick exam board + display name, then write to
   profiles and flip onboarding_complete. License gating then
   bounces license_none → /subscribe.                             */

export function Onboarding() {
  const nav = useNavigate();
  const { access, refreshAccess } = useAuth();
  const [board, setBoard] = React.useState("");
  const [name,  setName]  = React.useState("");
  const [busy,  setBusy]  = React.useState(false);
  const [err,   setErr]   = React.useState("");

  React.useEffect(() => {
    if (access?.profile.full_name) setName(access.profile.full_name);
  }, [access?.profile.full_name]);

  async function onContinue(e: React.FormEvent) {
    e.preventDefault();
    setErr("");
    if (!name.trim()) { setErr("Tell us what to call you."); return; }
    setBusy(true);
    try {
      await updateProfile({ full_name: name.trim() });
      // Persist board choice locally — schema may or may not have a column for it.
      try { localStorage.setItem("arabise-board", board || "Independent"); } catch {}
      await completeOnboarding();
      await refreshAccess();
      // Guard will now route to /subscribe (license_none) or /dashboard (license_personal).
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(humaniseAuthError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell eyebrow="Welcome" title="Welcome to Arabise" subtitle="Quick set-up so the leaderboard knows what to call you.">
      <Card>
        <CardContent>
          <form onSubmit={onContinue} className="space-y-5">
            <div>
              <h2 className="text-xs uppercase tracking-widest text-fg-3 mb-3">Pick your exam board</h2>
              <div className="grid grid-cols-2 gap-3">
                {BOARDS.map(b => (
                  <button key={b} type="button"
                    onClick={() => setBoard(b)}
                    aria-pressed={board === b}
                    className={
                      "rounded-2xl border p-4 text-left transition-colors group " +
                      (board === b
                        ? "border-accent bg-accent/10"
                        : "border-line bg-bg hover:border-line-2 hover:bg-bg-3")
                    }>
                    <GraduationCap className={"w-5 h-5 mb-2 " + (board === b ? "text-accent" : "text-fg-2")} />
                    <div className="font-semibold text-sm">{b}</div>
                    <div className="text-xs text-fg-3">GCSE Arabic</div>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-fg-3 mb-2">Display name</span>
              <Input placeholder="As shown on the leaderboard" value={name}
                onChange={e => setName(e.target.value)} maxLength={64} required />
            </label>

            {err && <ErrorBanner>{err}</ErrorBanner>}

            <Button type="submit" size="lg" className="w-full" disabled={busy}>
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
                    : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </form>
        </CardContent>
      </Card>

      <p className="mt-6 text-center text-xs text-fg-3">You can change all of this later in Profile.</p>
    </AuthShell>
  );
}

/* ───────────────────────── MFA Verify ─────────────────────────
   Verifies an 8-digit email OTP via supabase.auth.verifyOtp().    */

export function MfaVerify() {
  const nav = useNavigate();
  const { user, refreshAccess } = useAuth();
  const [digits, setDigits] = React.useState<string[]>(Array(8).fill(""));
  const [busy,   setBusy]   = React.useState(false);
  const [resending, setResending] = React.useState(false);
  const [msg,    setMsg]    = React.useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const inputsRef = React.useRef<(HTMLInputElement | null)[]>([]);

  function setDigit(i: number, val: string) {
    const cleaned = val.replace(/\D/g, "").slice(0, 1);
    setDigits(d => { const c = [...d]; c[i] = cleaned; return c; });
    if (cleaned && i < 7) inputsRef.current[i + 1]?.focus();
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
  }

  function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const txt = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 8);
    if (txt.length === 8) {
      e.preventDefault();
      setDigits(txt.split(""));
      inputsRef.current[7]?.focus();
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const code = digits.join("");
    if (code.length !== 8) { setMsg({ kind: "err", text: "Enter all 8 digits." }); return; }
    if (!user?.email)      { setMsg({ kind: "err", text: "No email on file." }); return; }
    setBusy(true); setMsg(null);
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: user.email,
        token: code,
        type:  "email"
      });
      if (error) throw error;
      await refreshAccess();
      nav("/dashboard", { replace: true });
    } catch (e) {
      setMsg({ kind: "err", text: humaniseAuthError(e) });
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    if (!user?.email) return;
    setResending(true); setMsg(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({ email: user.email });
      if (error) throw error;
      setMsg({ kind: "ok", text: "Code resent. Check your inbox." });
    } catch (e) {
      setMsg({ kind: "err", text: humaniseAuthError(e) });
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthShell eyebrow="Verify device" title="Enter your 8-digit code" subtitle="We emailed it to your inbox. Valid for 10 minutes.">
      <Card>
        <CardContent>
          <form onSubmit={onSubmit}>
            <div className="flex items-center justify-center gap-2">
              {digits.map((d, i) => (
                <input key={i}
                  ref={el => { inputsRef.current[i] = el; }}
                  value={d} onChange={e => setDigit(i, e.target.value)}
                  onKeyDown={e => onKeyDown(i, e)}
                  onPaste={onPaste}
                  maxLength={1} inputMode="numeric" autoComplete="one-time-code"
                  className="w-10 h-12 text-center rounded-lg border border-line bg-bg text-lg font-bold focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent" />
              ))}
            </div>

            {msg && <div role="alert" className={
              "mt-4 inline-flex items-center gap-2 text-sm w-full justify-center " +
              (msg.kind === "ok" ? "text-ok" : "text-bad")
            }>
              {msg.kind === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
              {msg.text}
            </div>}

            <Button type="submit" size="lg" className="w-full mt-6" disabled={busy}>
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</>
                    : <>Verify <ShieldCheck className="w-4 h-4" /></>}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button type="button" disabled={resending} onClick={onResend}
              className="text-xs text-fg-2 hover:text-fg disabled:opacity-60">
              {resending ? "Sending…" : "Didn't get a code? Resend"}
            </button>
          </div>
        </CardContent>
      </Card>
      <div className="mt-6 text-center">
        <Link to="/login" className="text-xs text-fg-2 hover:text-fg">← Back to sign in</Link>
      </div>
    </AuthShell>
  );
}

/* ───────────────────────── Reset Password ─────────────────────
   Single page handles three modes:
     - request a reset link
     - set a new password (recovery session detected)
     - expired link                                              */

export function ResetPassword() {
  const nav = useNavigate();
  const [mode, setMode] = React.useState<"request" | "set">("request");
  const [email, setEmail] = React.useState("");
  const [pw, setPw]       = React.useState("");
  const [pw2, setPw2]     = React.useState("");
  const [busy, setBusy]   = React.useState(false);
  const [sent, setSent]   = React.useState(false);
  const [msg, setMsg]     = React.useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // If we land here from a recovery email, supabase's
  // detectSessionInUrl reads the hash + we get a PASSWORD_RECOVERY
  // event. Listen for it and switch into "set" mode.
  React.useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(event => {
      if (event === "PASSWORD_RECOVERY") setMode("set");
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + window.location.pathname + "#/reset-password"
      });
      if (error) throw error;
      setSent(true);
    } catch (e) {
      setMsg({ kind: "err", text: humaniseAuthError(e) });
    } finally {
      setBusy(false);
    }
  }

  async function onSet(e: React.FormEvent) {
    e.preventDefault();
    if (pw.length < 8)  { setMsg({ kind: "err", text: "At least 8 characters." }); return; }
    if (pw !== pw2)     { setMsg({ kind: "err", text: "Passwords don't match." }); return; }
    setBusy(true); setMsg(null);
    try {
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw error;
      setMsg({ kind: "ok", text: "Password updated. Signing you in…" });
      setTimeout(() => nav("/dashboard", { replace: true }), 600);
    } catch (e) {
      setMsg({ kind: "err", text: humaniseAuthError(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      eyebrow={mode === "set" ? "Recovery" : "Forgot password"}
      title={mode === "set" ? "Set a new password" : "Reset your password"}
      subtitle={mode === "set"
        ? "Pick something hard to guess. Eight characters minimum."
        : "Pop in your email and we'll send a reset link."}>
      <Card>
        <CardContent>
          {mode === "set" ? (
            <form onSubmit={onSet} className="space-y-4">
              <Field label="New password" icon={<Lock className="w-4 h-4" />}>
                <Input type="password" placeholder="At least 8 characters"
                  className="pl-10" value={pw} onChange={e => setPw(e.target.value)} required />
              </Field>
              <Field label="Confirm" icon={<Lock className="w-4 h-4" />}>
                <Input type="password" placeholder="Type it again"
                  className="pl-10" value={pw2} onChange={e => setPw2(e.target.value)} required />
              </Field>
              {msg && <Banner msg={msg} />}
              <Button type="submit" size="lg" className="w-full" disabled={busy}>
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>
                      : <>Set password <CheckCircle2 className="w-4 h-4" /></>}
              </Button>
            </form>
          ) : sent ? (
            <div className="text-center py-3">
              <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-3" />
              <h3 className="font-semibold">Check your inbox</h3>
              <p className="mt-2 text-sm text-fg-2">
                If an account exists for <span className="text-fg">{email}</span>, the link is on its way.
              </p>
            </div>
          ) : (
            <form onSubmit={onRequest} className="space-y-4">
              <Field label="Email" icon={<Mail className="w-4 h-4" />}>
                <Input type="email" placeholder="you@example.com" autoFocus
                  className="pl-10" value={email} onChange={e => setEmail(e.target.value)} required />
              </Field>
              {msg && <Banner msg={msg} />}
              <Button type="submit" size="lg" className="w-full" disabled={busy}>
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                      : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
      <div className="mt-6 text-center">
        <Link to="/login" className="text-xs text-fg-2 hover:text-fg">← Back to sign in</Link>
      </div>
    </AuthShell>
  );
}

/* ───────────────────────── Signed Out ───────────────────────── */

export function SignedOut() {
  return (
    <AuthShell eyebrow="For your security" title="You've been signed out" subtitle="Close any other open Arabise tabs to clear cached data.">
      <Card>
        <CardContent className="text-center">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent/10 border border-accent-lo text-accent mx-auto mb-4">
            <ShieldCheck className="w-6 h-6" />
          </span>
          <p className="text-sm text-fg-2 leading-relaxed">
            Your session was ended cleanly. Sign in again to keep revising.
          </p>
          <Button size="lg" asChild className="mt-6 w-full">
            <Link to="/login">Sign in again <ArrowRight className="w-4 h-4" /></Link>
          </Button>
          <Button variant="ghost" size="md" asChild className="mt-2 w-full">
            <Link to="/">Back to home</Link>
          </Button>
        </CardContent>
      </Card>
    </AuthShell>
  );
}

/* ───────────────────────── 404 ───────────────────────── */

export function NotFound() {
  return (
    <PageFrame surface="marketing">
      <div className="min-h-screen pt-32 pb-16 px-6 flex items-center justify-center">
        <div className="text-center max-w-xl">
          <BlurReveal>
            <img src="/logo.png" alt="" className="h-16 w-16 mx-auto rounded-2xl mb-6" />
            <div className="text-xs uppercase tracking-widest text-accent">404 — Not found</div>
            <h1 className="mt-3 text-5xl md:text-7xl font-bold tracking-tight">Lost in translation.</h1>
            <p className="mt-5 text-fg-2 leading-relaxed">
              The page you were looking for isn't here. It might've moved, or never existed.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Button size="lg" asChild><Link to="/">Back to home <ArrowRight className="w-4 h-4" /></Link></Button>
              <Button variant="outline" size="lg" asChild><Link to="/dashboard">Go to dashboard</Link></Button>
            </div>
            <div className="mt-10 inline-flex items-center gap-2 text-xs text-fg-3">
              <Sparkles className="w-3 h-3 text-accent" />
              While you're here — <Link to="/learn" className="text-fg-2 hover:text-fg inline-flex items-center gap-1">go learn a word <BookOpen className="w-3 h-3" /></Link>
            </div>
          </BlurReveal>
        </div>
      </div>
    </PageFrame>
  );
}

/* ───────────────────────── tiny helpers ───────────────────────── */

function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-fg-3 mb-2">{label}</span>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-3 pointer-events-none">{icon}</span>
        {children}
      </div>
    </label>
  );
}

function ErrorBanner({ children }: { children: React.ReactNode }) {
  return (
    <div role="alert" className="flex items-start gap-2 rounded-lg border border-bad/40 bg-bad/10 px-3 py-2 text-sm text-bad">
      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{children}</span>
    </div>
  );
}

function Banner({ msg }: { msg: { kind: "ok" | "err"; text: string } }) {
  return (
    <div role="alert" className={
      "flex items-start gap-2 rounded-lg px-3 py-2 text-sm " +
      (msg.kind === "ok"
        ? "border border-ok/40 bg-ok/10 text-ok"
        : "border border-bad/40 bg-bad/10 text-bad")
    }>
      {msg.kind === "ok" ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
      <span>{msg.text}</span>
    </div>
  );
}

const BOARDS = ["AQA", "Edexcel", "OCR (Self-study)", "Independent"];
